/**
 * Entity Field Prioritizer
 * Intelligently prioritizes entity fields for display based on importance
 */

export interface FieldPriorityConfig {
  identifierFields?: string[];
  measurementFields?: string[];
  locationFields?: string[];
  depthFields?: string[];
  dataSourceFields?: string[];
  otherImportantFields?: string[];
}

const DEFAULT_IDENTIFIER_FIELDS = [
  'sample_id', 'location_id', 'well_id', 'qc_sample_id', 'measurement_id',
  'sample_date', 'collection_date', 'measurement_date', 'analysis_date',
  'id', 'entity_id', 'name', 'title',
];

const DEFAULT_MEASUREMENT_FIELDS = [
  'total_PFAS_ugL', 'total_PFAS_ugkg',
  'ORP_mV', 'DO_mgL', 'pH', 'temperature_C', 'conductivity_uScm', 'turbidity_NTU',
  'water_level_m', 'water_elevation_m',
  'rpd_percent', 'contamination_detected', 'acceptance_criteria_met',
  'health_canada_exceedance', 'epa_mcl_exceedance',
  'unit_validation_status', 'units_normalized',
  'value', 'measurement', 'result', 'concentration',
];

const DEFAULT_LOCATION_FIELDS = [
  'utm_x', 'utm_y', 'latitude', 'longitude', 'aec_area', 'site_name',
  'location', 'coordinates', 'address',
];

const DEFAULT_DEPTH_FIELDS = [
  'depth_top_m', 'depth_bottom_m', 'depth_midpoint_m', 'depth_source',
  'well_depth_m', 'screen_top_m', 'screen_bottom_m',
  'depth', 'elevation',
];

const DEFAULT_DATA_SOURCE_FIELDS = [
  'data_source_type', 'data_source_file', 'data_source_priority',
  'laboratory', 'analytical_method', 'coa_number',
  'source', 'source_file', 'source_type',
];

const DEFAULT_OTHER_IMPORTANT_FIELDS = [
  'matrix_type', 'qc_type', 'units', 'TEA_zone', 'TEA_confidence',
  'sampling_method', 'measurement_method', 'notes',
  'status', 'type', 'category',
];

/**
 * Get prioritized field keys from entities
 */
export function getPrioritizedFields(
  entities: Array<{ data?: Record<string, any> }>,
  config?: FieldPriorityConfig
): string[] {
  if (entities.length === 0) return [];
  
  // Collect all keys from all entities
  const allKeys = new Set<string>();
  entities.forEach(entity => {
    if (entity.data) {
      Object.keys(entity.data).forEach(key => allKeys.add(key));
    }
  });
  
  const keys = Array.from(allKeys);
  
  // Use provided config or defaults
  const identifierFields = config?.identifierFields || DEFAULT_IDENTIFIER_FIELDS;
  const measurementFields = config?.measurementFields || DEFAULT_MEASUREMENT_FIELDS;
  const locationFields = config?.locationFields || DEFAULT_LOCATION_FIELDS;
  const depthFields = config?.depthFields || DEFAULT_DEPTH_FIELDS;
  const dataSourceFields = config?.dataSourceFields || DEFAULT_DATA_SOURCE_FIELDS;
  const otherImportantFields = config?.otherImportantFields || DEFAULT_OTHER_IMPORTANT_FIELDS;
  
  // Sort keys by priority
  const sortedKeys: string[] = [];
  
  // 1. Identifiers first
  identifierFields.forEach(k => {
    if (keys.includes(k)) sortedKeys.push(k);
  });
  
  // 2. Measurement/result fields (most important for display)
  measurementFields.forEach(k => {
    if (keys.includes(k)) sortedKeys.push(k);
  });
  
  // 3. Location fields
  locationFields.forEach(k => {
    if (keys.includes(k)) sortedKeys.push(k);
  });
  
  // 4. Depth fields
  depthFields.forEach(k => {
    if (keys.includes(k)) sortedKeys.push(k);
  });
  
  // 5. Other important fields
  otherImportantFields.forEach(k => {
    if (keys.includes(k)) sortedKeys.push(k);
  });
  
  // 6. Data source fields
  dataSourceFields.forEach(k => {
    if (keys.includes(k)) sortedKeys.push(k);
  });
  
  // 7. Any remaining fields (exclude null/empty arrays/objects)
  keys.forEach(k => {
    if (!sortedKeys.includes(k)) {
      // Check if field has meaningful data in at least one entity
      const hasData = entities.some(e => {
        const value = e.data?.[k];
        if (value === null || value === undefined) return false;
        if (Array.isArray(value) && value.length === 0) return false;
        if (typeof value === 'object' && Object.keys(value).length === 0) return false;
        return true;
      });
      if (hasData) sortedKeys.push(k);
    }
  });
  
  return sortedKeys;
}











